import React, { useCallback, useState, useEffect } from 'react';
import { PlusCircle, Archive, HardDrive, X, Pencil, Trash2 } from 'lucide-react';
import { dataVaultService, type DataVault } from '../../../../components/api/dataVaultService';

type VaultType = 'Structured' | 'File Base';

const formatSize = (bytes?: number): string => {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const DataVaults: React.FC = () => {
  const [vaults, setVaults] = useState<DataVault[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<VaultType>('Structured');
  const [newIsActive, setNewIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [editingVault, setEditingVault] = useState<DataVault | null>(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState<VaultType>('Structured');
  const [editIsActive, setEditIsActive] = useState(true);
  const [vaultToDelete, setVaultToDelete] = useState<DataVault | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchVaults = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dataVaultService.getList();
      setVaults(Array.isArray(res) ? res : []);
    } catch {
      setError('Failed to load data vaults.');
      setVaults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVaults();
  }, [fetchVaults]);

  const handleAddVault = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      await dataVaultService.create({
        name: newName.trim(),
        type: newType,
        isActive: newIsActive,
        dataSizeBytes: 0,
      });
      setNewName('');
      setNewType('Structured');
      setNewIsActive(true);
      setShowAddModal(false);
      await fetchVaults();
    } catch (err: any) {
      setSaveError(err?.response?.data?.message || err?.message || 'Failed to save vault.');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (v: DataVault) => {
    setEditingVault(v);
    setEditName(v.name);
    setEditType((v.type as VaultType) || 'Structured');
    setEditIsActive(v.isActive ?? true);
    setSaveError(null);
  };

  const handleEditVault = async () => {
    if (!editingVault || !editName.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      await dataVaultService.update(editingVault.id, {
        name: editName.trim(),
        type: editType,
        isActive: editIsActive,
        dataSizeBytes: editingVault.dataSizeBytes ?? 0,
      });
      setEditingVault(null);
      await fetchVaults();
    } catch (err: any) {
      setSaveError(err?.response?.data?.message || err?.message || 'Failed to update vault.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVault = async () => {
    if (!vaultToDelete) return;
    setDeleting(true);
    setSaveError(null);
    try {
      await dataVaultService.delete(vaultToDelete.id);
      setVaultToDelete(null);
      await fetchVaults();
    } catch (err: any) {
      setSaveError(err?.response?.data?.message || err?.message || 'Failed to delete vault.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">Data Vaults</h2>
          <p className="text-sm text-gray-400 mt-1">
            Create and manage vaults. Vault type: Structured or File Base. View data size.
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm shadow"
        >
          <PlusCircle className="w-4 h-4 mr-2" /> Add New Vault
        </button>
      </div>

      {error && <p className="mb-3 text-sm text-red-400">{error}</p>}

      {loading ? (
        <div className="text-center text-gray-500 py-10">Loading…</div>
      ) : vaults.length === 0 ? (
        <div className="text-center text-gray-500 py-10">No vaults yet. Add one to get started.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {vaults.map((v) => (
            <div
              key={v.id}
              className="bg-neutral-900 border border-neutral-800 rounded-lg p-5 shadow hover:border-indigo-500 transition-all"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  {v.type === 'File Base' ? (
                    <HardDrive className="w-5 h-5 text-amber-400" />
                  ) : (
                    <Archive className="w-5 h-5 text-indigo-400" />
                  )}
                  <h3 className="text-white font-medium text-base">{v.name}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs px-2 py-1 rounded-full font-semibold ${v.isActive ? 'bg-green-700 text-green-100' : 'bg-red-700 text-red-100'}`}
                  >
                    {v.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <button
                    onClick={() => openEditModal(v)}
                    className="p-1.5 text-gray-400 hover:text-indigo-400 hover:bg-neutral-800 rounded"
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setVaultToDelete(v)}
                    className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-neutral-800 rounded"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="text-sm text-gray-300 space-y-1">
                <p>
                  <span className="text-gray-400">Type:</span> {v.type}
                </p>
                <p>
                  <span className="text-gray-400">Data size:</span>{' '}
                  <span className="font-mono text-indigo-300">{formatSize(v.dataSizeBytes)}</span>
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex justify-center items-center p-4">
          <div className="bg-neutral-900 w-full max-w-md rounded-xl shadow-xl border border-neutral-800">
            <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-800">
              <h2 className="text-white text-lg font-semibold">Add New Vault</h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-white" disabled={saving}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm text-white">
              {saveError && <p className="text-red-400 text-sm">{saveError}</p>}
              <div>
                <label className="text-gray-400 block mb-1">Vault Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Customer Data Vault"
                  className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                />
              </div>
              <div>
                <label className="text-gray-400 block mb-1">Type</label>
                <select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value as VaultType)}
                  className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                >
                  <option value="Structured">Structured</option>
                  <option value="File Base">File Base</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={newIsActive}
                  onChange={() => setNewIsActive(!newIsActive)}
                  className="accent-indigo-500"
                />
                <label className="text-gray-300">Set as active</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-800 bg-neutral-950 rounded-b-xl">
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-white text-sm" disabled={saving}>
                Cancel
              </button>
              <button
                onClick={handleAddVault}
                disabled={!newName.trim() || saving}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-md text-sm shadow disabled:opacity-40"
              >
                {saving ? 'Saving…' : 'Save Vault'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingVault && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex justify-center items-center p-4">
          <div className="bg-neutral-900 w-full max-w-md rounded-xl shadow-xl border border-neutral-800">
            <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-800">
              <h2 className="text-white text-lg font-semibold">Edit Vault</h2>
              <button onClick={() => setEditingVault(null)} className="text-gray-400 hover:text-white" disabled={saving}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm text-white">
              {saveError && <p className="text-red-400 text-sm">{saveError}</p>}
              <div>
                <label className="text-gray-400 block mb-1">Vault Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="e.g. Customer Data Vault"
                  className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                />
              </div>
              <div>
                <label className="text-gray-400 block mb-1">Type</label>
                <select
                  value={editType}
                  onChange={(e) => setEditType(e.target.value as VaultType)}
                  className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                >
                  <option value="Structured">Structured</option>
                  <option value="File Base">File Base</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={editIsActive}
                  onChange={() => setEditIsActive(!editIsActive)}
                  className="accent-indigo-500"
                />
                <label className="text-gray-300">Set as active</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-800 bg-neutral-950 rounded-b-xl">
              <button onClick={() => setEditingVault(null)} className="text-gray-400 hover:text-white text-sm" disabled={saving}>
                Cancel
              </button>
              <button
                onClick={handleEditVault}
                disabled={!editName.trim() || saving}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-md text-sm shadow disabled:opacity-40"
              >
                {saving ? 'Saving…' : 'Update Vault'}
              </button>
            </div>
          </div>
        </div>
      )}

      {vaultToDelete && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex justify-center items-center p-4">
          <div className="bg-neutral-900 w-full max-w-md rounded-xl shadow-xl border border-neutral-800">
            <div className="px-6 py-4 border-b border-neutral-800">
              <h2 className="text-white text-lg font-semibold">Delete Vault</h2>
            </div>
            <div className="p-6 text-sm text-gray-300">
              {saveError && <p className="text-red-400 text-sm mb-3">{saveError}</p>}
              <p>
                Are you sure you want to delete <span className="text-white font-medium">{vaultToDelete.name}</span>? This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-800 bg-neutral-950 rounded-b-xl">
              <button onClick={() => setVaultToDelete(null)} className="text-gray-400 hover:text-white text-sm" disabled={deleting}>
                Cancel
              </button>
              <button
                onClick={handleDeleteVault}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-md text-sm shadow disabled:opacity-40"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataVaults;
